import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import VenueMap from "../components/VenueMap";
import ShareButton from "../components/ShareButton";
import { Clock, Beer, Flame, ArrowLeft, MapPin } from "lucide-react";

const DIFFICULTY_COLOR = {
  facil: "text-[#00FF66]",
  intermedio: "text-[#FEBA01]",
  hardcore: "text-[#FF3333]",
};

export default function RouteDetail() {
  const { id } = useParams();
  const [route, setRoute] = useState(null);

  useEffect(() => {
    api.getRoute(id).then(setRoute).catch(() => {});
  }, [id]);

  if (!route) {
    return (
      <div className="max-w-3xl mx-auto p-12">
        <div className="beer-pour" />
      </div>
    );
  }

  const venues = route.venues || [];

  return (
    <div data-testid="route-detail-page">
      <section className="relative h-[40vh] min-h-[280px] overflow-hidden border-b-2 border-[#1A1A1A]">
        {route.cover_image && <img src={route.cover_image} alt={route.name} className="w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-black/70 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 max-w-7xl mx-auto px-4 lg:px-8 pb-6">
          <Link to="/rutas" className="inline-flex items-center gap-1 text-[#A3A3A3] hover:text-[#FEBA01] font-mono text-xs uppercase tracking-widest mb-3">
            <ArrowLeft size={14} /> Rutas
          </Link>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {route.is_official && <span className="bg-[#FEBA01] text-black font-display text-xs uppercase px-2 py-1">Top Kiltro</span>}
            {route.difficulty && (
              <span className={`bg-black border border-current ${DIFFICULTY_COLOR[route.difficulty]} font-mono text-[10px] uppercase tracking-widest px-2 py-1 inline-flex items-center gap-1`}>
                <Flame size={10} /> {route.difficulty}
              </span>
            )}
          </div>
          <h1 className="font-display text-5xl sm:text-7xl uppercase tracking-tighter leading-[0.9] text-white">{route.name}</h1>
          <div className="mt-2 flex gap-4 font-mono text-xs uppercase tracking-widest text-[#A3A3A3]">
            <span className="flex items-center gap-1"><Beer size={12} /> {venues.length} paradas</span>
            {route.duration_hours && <span className="flex items-center gap-1"><Clock size={12} /> {route.duration_hours}h</span>}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 space-y-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <p className="text-lg text-[#F5F5F5] max-w-3xl">{route.description}</p>
          <ShareButton kind="route" id={route.id} testid="route-share-btn" />
        </div>

        <VenueMap venues={venues} numbered showLine height={460} />

        <section>
          <h2 className="font-display text-3xl uppercase tracking-tight mb-4">El recorrido</h2>
          <div className="space-y-3">
            {venues.map((v, i) => (
              <Link
                key={v.id}
                to={`/local/${v.id}`}
                data-testid={`route-stop-${i}`}
                className="flex flex-col sm:flex-row gap-4 bg-[#1A1A1A] border-2 border-[#333] hover:border-[#FEBA01] p-4 transition-colors group"
              >
                <div className="flex items-center gap-3 sm:w-1/3">
                  <div className="w-10 h-10 bg-[#FEBA01] text-black flex items-center justify-center font-display text-xl shrink-0">{i + 1}</div>
                  {v.cover_photo && <img src={v.cover_photo} alt="" className="w-20 h-20 object-cover" />}
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-2xl uppercase leading-none tracking-tight group-hover:text-[#FEBA01]">{v.name}</h3>
                  <div className="mt-1 flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3]">
                    <MapPin size={10} /> {v.neighborhood} · {v.address}
                  </div>
                  <p className="mt-2 text-sm text-[#A3A3A3] line-clamp-2">{v.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
